<template>
    <component
        :is="tooltipComponent"
        v-bind="tooltipProps"
    >
        <Button
            color="carbon-400"
            :aria-label="$tr('gameContainer.share')"
            interfaceTarget="game-share"
            :class="$style.button_override"
            @click="handleShare"
        >
            <Icon name="send" />
            <Typography
                size="m"
                type="label"
                accent
                :responsive="false"
                :class="$style.button_label"
            >
                {{ $tr('gameContainer.share') }}
            </Typography>
        </Button>
        <template #content>
            <TooltipContent
                v-if="isDesktop"
                :title="$tr('gameContainer.share_popup_tooltip_title')"
                :image="props.gameIcon"
            >
                <div :class="$style.share_buttons">
                    <IconButton
                        v-for="option in shareOptions"
                        :key="option.id"
                        color="carbon-400"
                        size="l"
                        :interfaceTarget="`share-to-${option.id}-button`"
                        :icon="option.icon"
                        :externalTo="option.url"
                        target="_blank"
                        @click="logShareTo(option.id)"
                    />
                </div>
                <div
                    :class="$style.copy_section"
                    data-interface-target="copy-share-link"
                    @click="handleShare"
                >
                    <Typography
                        size="s"
                        type="label"
                        :class="$style.link"
                        :responsive="false"
                    >
                        {{ shared ? $tr('gameContainer.share_popup_tooltip_copied') : displayedShareUrl }}
                    </Typography>
                    <IconButton
                        color="carbon-400"
                        size="s"
                        interfaceTarget="copy-share-link"
                        :class="$style.button_round_override"
                        icon="copy"
                    />
                </div>
            </TooltipContent>
        </template>
    </component>
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core'
import { computed } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import IconButton from '@/components/Buttons/IconButton.vue'
import { IconNameType } from '@/components/Icon'
import Icon from '@/components/Icon/Icon.vue'
import TooltipPopup from '@/components/Tooltip/TooltipPopup.vue'
import Typography from '@/components/Typography.vue'
import { useShareData } from '@/composables/use-share-data'
import { useTr } from '@/composables/use-tr'
import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'

import TooltipContent from './TooltipContent.vue'

const props = defineProps<{
    shareData: ShareData
    gameIcon: string
}>()

const isMounted = useMounted()
const tr = useTr()

const appStore = useAppStore()
const loggerStore = useLoggerStore()

const { share, shared } = useShareData({
    successDuration: 3000,
})

const isDesktop = computed(() => appStore.platform === 'desktop')
const displayedShareUrl = computed(() => {
    try {
        const url = new URL(props.shareData.url || '')
        return url.href.replace(`${url.protocol}//`, '')
    } catch {
        return props.shareData.url || ''
    }
})

const shareOptions = computed<Array<{ id: string; icon: IconNameType; url: string }>>(() => {
    const hashtag = '#playgama'
    const url = encodeURIComponent(props.shareData.url || '')
    const title = props.shareData.title || ''
    const twitterText = encodeURIComponent(tr('gameContainer.share_twitter_text', { title }))
    const redditText = encodeURIComponent(tr('gameContainer.share_reddit_text', { title }))

    return [
        {
            id: 'facebook',
            icon: 'facebook',
            url: `https://www.facebook.com/sharer/sharer.php?u=${url}&hashtag=${encodeURIComponent(hashtag)}`,
        },
        {
            id: 'twitter',
            icon: 'twitter',
            url: `https://twitter.com/intent/tweet?url=${url}&text=${twitterText}`,
        },
        {
            id: 'reddit',
            icon: 'reddit',
            url: `https://reddit.com/submit?url=${url}&title=${redditText}`,
        },
    ]
})

const tooltipComponent = computed(() => (isMounted.value ? TooltipPopup : 'div'))
const tooltipProps = computed(() => (isMounted.value ? { interfaceTarget: 'partner-button-tooltip' } : {}))

function logShareTo(id: string) {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'share',
        dimension1: `click-on-button-${id}`,
    })
}

function handleShare() {
    share(props.shareData, 'game-container-share-button')
}
</script>

<style module>
.share_buttons {
    display: flex;
    gap: 15px;
}

.copy_section {
    display: flex;
    gap: 16px;
    height: 48px;
    padding: 0 4px 0 12px;
    align-items: center;
    align-self: stretch;
    border-radius: 1000px;
    background: var(--carbon-200);
    cursor: pointer;
}

.link {
    flex: 1;
    color: #ffffff;
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.button_round_override {
    width: 40px;
    height: 40px;
}

.button_override {
    padding: 8px;
}

.button_label {
    display: none;
}

@media (--desktop-large-plus) {
    .button_override {
        padding: 13px 16px;
    }

    .button_label {
        display: block;
    }
}
</style>
